import {ReactNode, useId, useMemo} from 'react';
import {observer} from 'mobx-react';
import MPDSSwitcher from '../../mpds-switcher';
import NOFilter from '../no-filter';
import {FilterProps} from '../filter-types';

type Props = {
    // Current grain selection
    geographyGrain: 'COUNTY' | 'ZIP';
    onGeographyGrainChange: (grain: 'COUNTY' | 'ZIP') => void;

    // Geography selection
    countyValue: string;
    zipValue: string;
    onCountyChange: (value: string) => void;
    onZipChange: (value: string) => void;

    // Data sources
    countyData: string[];
    zipData: string[];

    // Loading and disabled states
    loading: boolean;
    disabled: boolean;

    // Test IDs
    switcherTestId?: string;
    dropdownTestId?: string;
};

/**
 * Geography filter with grain switcher (County/ZIP)
 *
 * Combines:
 * - MPDS switcher for County | ZIP selection
 * - Unlabelled dropdown that changes placeholder and data based on grain
 * - Automatic clearing of geography selection when grain changes (handled by parent)
 *
 * @example
 * <GeographyFilterWithGrain
 *   geographyGrain={comparisonStore.geographyGrain}
 *   onGeographyGrainChange={(grain) => comparisonStore.setGeographyGrain(grain)}
 *   countyValue={comparisonStore.comparisonFilters.county}
 *   zipValue={comparisonStore.comparisonFilters.zip}
 *   onCountyChange={(v) => comparisonStore.setComparisonFilter('county', v)}
 *   onZipChange={(v) => comparisonStore.setComparisonFilter('zip', v)}
 *   countyData={countyOptions}
 *   zipData={zipOptions}
 *   loading={noStore.loadingGeographyData}
 *   disabled={!state || !rosterId}
 * />
 */
const GeographyFilterWithGrain = observer((props: Props) => {
    const {
        geographyGrain,
        onGeographyGrainChange,
        countyValue,
        zipValue,
        onCountyChange,
        onZipChange,
        countyData,
        zipData,
        loading,
        disabled,
        switcherTestId = 'geography-grain-switcher',
        dropdownTestId,
    } = props;

    const isCountyGrain = geographyGrain === 'COUNTY';
    // Unique per instance so multiple filters on a page don't share the label id
    const grainLabelId = useId();

    // Build geography filter props based on current grain
    const geographyFilterProps: FilterProps = useMemo(() => {
        const data = isCountyGrain ? countyData : zipData;
        const value = isCountyGrain ? countyValue : zipValue;
        const onChange = isCountyGrain ? onCountyChange : onZipChange;
        // An enabled, loaded dropdown with no options would otherwise look broken
        // (e.g. ZIP values are not yet returned by the geography API)
        const noOptions = !loading && !disabled && data.length === 0;
        const placeholder = noOptions
            ? `No ${isCountyGrain ? 'counties' : 'ZIP codes'} available`
            : isCountyGrain ? 'Select a county' : 'Select a ZIP code';
        const testId = dropdownTestId || (isCountyGrain ? 'county-dropdown' : 'zip-dropdown');

        const children: [(item: any) => string, (item: any) => ReactNode] = [
            (item: any): string => item,
            (item: any): ReactNode => item || 'All',
        ];

        // No label: the Geography label and County | ZIP switcher above already name the dropdown
        return {
            data,
            value,
            onChange,
            children,
            spinner: loading,
            disabled: disabled || noOptions,
            placeholder,
            ['data-testid']: testId,
        };
    }, [
        isCountyGrain,
        countyData,
        zipData,
        countyValue,
        zipValue,
        onCountyChange,
        onZipChange,
        loading,
        disabled,
        dropdownTestId,
    ]);

    return (
        <>
            {/* Geography label with the grain switcher below it */}
            <div className='no-geo-head mb-2' data-testid='geography-grain-section'>
                {/* A label element can't target the switcher (a div), so it names it via aria-labelledby */}
                <span className='cds--label' id={grainLabelId}>
                    Geography
                </span>
                <MPDSSwitcher
                    options={[
                        {value: 'COUNTY', label: 'County', dataAttribute: 'county'},
                        {value: 'ZIP', label: 'ZIP', dataAttribute: 'zip'}
                    ]}
                    selectedValue={geographyGrain}
                    onChange={(value) => onGeographyGrainChange(value as 'COUNTY' | 'ZIP')}
                    ariaLabelledBy={grainLabelId}
                    className='mpds-switcher--low-contrast no-grain'
                    data-testid={switcherTestId}
                />
            </div>

            {/* Geography dropdown (County or ZIP based on grain) */}
            <NOFilter {...{...geographyFilterProps, searchable: true}} />
        </>
    );
});

export default GeographyFilterWithGrain;
